<script setup>
import { computed, ref } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import MessageStatus from 'dashboard/components-next/message/MessageStatus.vue';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';
import FileChip from 'dashboard/components-next/message/chips/File.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  isOwn: {
    type: Boolean,
    default: false,
  },
});

const galleryOpen = ref(false);
const activeGalleryAttachment = ref(null);

const readableTime = computed(() =>
  messageTimestamp(props.message.created_at, 'LLL d, h:mm a')
);

const status = computed(() => {
  if (!props.isOwn) return null;
  return props.message.read_at != null ? 'read' : 'sent';
});

const camelAttachments = computed(() =>
  (props.message.attachments || []).map(att => useCamelCase(att))
);

const imageAttachments = computed(() =>
  camelAttachments.value.filter(a => a.fileType === 'image')
);

const audioAttachments = computed(() =>
  camelAttachments.value.filter(a => a.fileType === 'audio')
);

const fileAttachments = computed(() =>
  camelAttachments.value.filter(
    a => a.fileType === 'file' || a.fileType === 'video'
  )
);

const allGalleryAttachments = computed(() =>
  imageAttachments.value.map(a => useSnakeCase(a))
);

const activeGallerySnake = computed(() =>
  activeGalleryAttachment.value
    ? useSnakeCase(activeGalleryAttachment.value)
    : null
);

const openGallery = att => {
  activeGalleryAttachment.value = att;
  galleryOpen.value = true;
};
</script>

<template>
  <div
    class="flex items-end gap-2 max-w-[75%] mb-1"
    :class="isOwn ? 'ml-auto flex-row-reverse' : 'mr-auto'"
  >
    <Avatar
      v-if="!isOwn"
      :src="message.sender.avatar_url"
      :name="message.sender.name"
      :size="28"
      class="shrink-0 mb-0.5"
    />
    <div
      class="flex flex-col gap-0.5"
      :class="isOwn ? 'items-end' : 'items-start'"
    >
      <div
        class="rounded-xl px-3.5 py-2 text-sm leading-relaxed break-words"
        :class="
          isOwn
            ? 'bg-n-solid-blue text-n-slate-12 ltr:rounded-br-sm rtl:rounded-bl-sm'
            : 'bg-n-slate-4 text-n-slate-12 ltr:rounded-bl-sm rtl:rounded-br-sm'
        "
      >
        <p v-if="message.content" class="m-0 whitespace-pre-wrap">
          {{ message.content }}
        </p>

        <!-- Images -->
        <div v-if="imageAttachments.length" class="flex flex-wrap gap-1 mt-1">
          <div
            v-for="att in imageAttachments"
            :key="att.id"
            class="size-[72px] overflow-hidden rounded-xl cursor-pointer"
            @click="openGallery(att)"
          >
            <img
              class="object-cover w-full h-full"
              :src="att.thumbUrl || att.dataUrl"
              :alt="att.filename"
            />
          </div>
        </div>

        <!-- Audio -->
        <AudioChip
          v-for="att in audioAttachments"
          :key="att.id"
          :attachment="att"
          class="mt-1 bg-n-alpha-3 dark:bg-n-alpha-2 text-n-slate-12"
        />

        <!-- Files / Video -->
        <FileChip
          v-for="att in fileAttachments"
          :key="att.id"
          :attachment="att"
          class="mt-1"
        />

        <!-- Timestamp + status inside bubble -->
        <div class="flex items-center justify-end gap-1 mt-1">
          <span class="text-[10px] leading-none text-n-slate-11">
            {{ readableTime }}
          </span>
          <MessageStatus v-if="status" :status="status" />
        </div>
      </div>
    </div>
  </div>

  <GalleryView
    v-if="galleryOpen && activeGallerySnake"
    v-model:show="galleryOpen"
    :attachment="activeGallerySnake"
    :all-attachments="allGalleryAttachments"
    @close="galleryOpen = false"
  />
</template>
